import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { jsPDF as JsPdfConstructor } from 'jspdf';
import { normalizeMaterialMode } from '../domain/materialMode';
import type {
  ClientOfferDTO,
  ClientOfferSurfaceSummary,
} from '../domain/clientOffer.types';
import { sanitizeClientOfferDTO } from '../domain/clientOffer';
import { getMissingContractorProfileFields } from '../domain/contractorProfile';
import {
  TEST_OFFER_CONTRACTOR_LINE,
  TEST_OFFER_DISCLAIMER,
  TEST_OFFER_TITLE,
  type OfferDocumentVariant,
} from '../domain/offerDocumentVariant';
import { buildClientOfferItemRows, getClientMaterialModeLabel, type ClientOfferItemRow } from '../domain/clientOfferPresentation';
export { buildClientOfferItemRows, type ClientOfferItemRow } from '../domain/clientOfferPresentation';
import { MINIMUM_VISIT_PRICING_NOTE } from '../domain/estimate.rates';
import { buildClientPdfSummaryRows } from '../pdf/buildClientPdfSummaryRows';
import { addRobotoFont } from './fonts';
import { formatProjectScopeLabel } from './projectDisplay';

export type ClientOfferPdfData = ClientOfferDTO;
export type {
  ClientOfferContractorData,
  ClientOfferDTO,
  ClientOfferLineItem,
  ClientOfferSurfaceSummary,
} from '../domain/clientOffer.types';

export interface ClientOfferPdfArtifact {
  doc: jsPDF;
  blob: Blob;
  file: File | null;
  fileName: string;
}

export interface ClientOfferStructureSnapshot {
  projectName: string;
  materialMode: ReturnType<typeof normalizeMaterialMode>;
  documentVariant: OfferDocumentVariant;
  areaRows: string[][];
  itemRows: ClientOfferItemRow[];
  summaryRows: Array<{ label: string; value: string }>;
  materialNote?: string;
  /** Present only when a minimum-entry item is actually on the offer. */
  minimumVisitNote?: string;
  /** The markings that make a test document unmistakably a test document. */
  testNotices: string[];
}

/** Every line that marks the page as a draft, in the order the PDF prints them. */
export function resolveTestOfferNotices(data: ClientOfferPdfData): string[] {
  if (data.documentVariant !== 'test') return [];
  return [TEST_OFFER_TITLE, TEST_OFFER_CONTRACTOR_LINE, TEST_OFFER_DISCLAIMER];
}

/**
 * The minimum-entry note the client PDF prints, or nothing when no such item is
 * on the offer. Exported so a regression test can assert the exact wording that
 * reaches the client, on the same path the PDF uses.
 */
export function resolveMinimumVisitNote(data: ClientOfferPdfData): string | undefined {
  const hasMinimumVisitItem = data.items.some((item) =>
    normalizeText(item.name).includes('minimalny koszt wejscia')
  );

  return hasMinimumVisitItem ? MINIMUM_VISIT_PRICING_NOTE : undefined;
}

const COLORS = {
  dark: [15, 23, 42] as [number, number, number],
  green: [5, 150, 105] as [number, number, number],
  light: [248, 250, 252] as [number, number, number],
};

export function createClientOfferPdfArtifact(data: ClientOfferPdfData): ClientOfferPdfArtifact {
  const clientData = sanitizeClientOfferDTO(data);
  const doc = buildClientOfferPDFDocument(clientData);
  const fileName = buildClientOfferPdfFileName(clientData);
  const blob = doc.output('blob');
  const file = typeof File === 'undefined'
    ? null
    : new File([blob], fileName, { type: 'application/pdf' });

  return { doc, blob, file, fileName };
}

export function generateClientOfferPDF(data: ClientOfferPdfData): void {
  const artifact = createClientOfferPdfArtifact(data);
  artifact.doc.save(artifact.fileName);
}

function buildClientOfferPdfFileName(data: ClientOfferPdfData): string {
  // A draft must not arrive in the client's downloads folder named "Oferta_".
  const namePrefix = data.documentVariant === 'test' ? 'Roboczy_PDF_testowy' : 'Oferta';
  return `${namePrefix}_${data.projectName.replace(/\s+/g, '_')}_${data.date.toISOString().split('T')[0]}.pdf`;
}

export function buildClientOfferPDFDocument(data: ClientOfferPdfData): jsPDF {
  data = sanitizeClientOfferDTO(data);
  if (data.vatLabel === 'VAT: do potwierdzenia') {
    throw new Error('UNRESOLVED_VAT_MODE');
  }
  // The contractor profile guard still stands — for the document that carries a
  // contractor's name. A test document prints no identity, so there is nothing
  // to validate and nothing a missing profile could misrepresent.
  if (data.documentVariant !== 'test') {
    const missingContractorData = getMissingContractorProfileFields(data.contractor);
    if (missingContractorData.length > 0) {
      throw new Error(`MISSING_CONTRACTOR_PROFILE:${missingContractorData.join(',')}`);
    }
  }

  const doc = new JsPdfConstructor({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const fontName = addRobotoFont(doc);
  const isTestDocument = data.documentVariant === 'test';
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 16;
  let y = 18;
  const priceSummary = buildClientPdfSummaryRows({
    laborNet: data.workNet,
    materialsNet: data.materialNet,
    netTotal: data.netTotal,
    vatLabel: data.vatLabel,
    vatRate: data.vatPercent,
    vatValue: data.vatAmount,
    grossTotal: data.grossTotal,
    materialMode: data.materialMode,
  });

  y = drawContractorHeader(doc, fontName, data, margin, pageW, y);

  if (data.documentVariant === 'test') {
    y = drawNote(doc, fontName, y, margin, TEST_OFFER_DISCLAIMER, isTestDocument);
  }

  y = drawSectionTitle(doc, fontName, y, margin, 'Podsumowanie projektu');
  y = drawKeyValue(doc, fontName, y, margin, 'Projekt', data.projectName);
  if (data.clientName.trim()) {
    y = drawKeyValue(doc, fontName, y, margin, 'Klient', data.clientName);
  }
  y = drawKeyValue(doc, fontName, y, margin, 'Data', data.date.toLocaleDateString('pl-PL'));
  y = drawKeyValue(doc, fontName, y, margin, 'Rozliczenie', data.settlementLabel);
  y = drawKeyValue(doc, fontName, y, margin, 'Materiały', getClientMaterialModeLabel(data.materialMode));
  if (data.regionLabel) {
    y = drawKeyValue(doc, fontName, y, margin, 'Region', data.regionLabel);
  }
  y += 5;

  y = drawSectionTitle(doc, fontName, y, margin, 'Zakres i powierzchnie');
  autoTable(doc, {
    startY: y,
    body: buildAreaRows(data),
    margin: { left: margin, right: margin },
    styles: { font: fontName, fontSize: 9, cellPadding: isTestDocument ? 1.5 : 2, overflow: 'linebreak' },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 74 },
      1: { cellWidth: pageW - margin * 2 - 74 },
    },
    alternateRowStyles: { fillColor: COLORS.light },
  });
  y = getLastTableY(doc) + (isTestDocument ? 4 : TABLE_GAP_AFTER);

  y = drawSectionTitle(doc, fontName, y, margin, isTestDocument ? 'Pozycje wyceny' : 'Pozycje oferty', isTestDocument);
  autoTable(doc, {
    startY: y,
    head: [['Pozycja', 'Ilość', 'Jm.', 'Cena jedn.', 'Wartość']],
    body: buildClientOfferItemRows(data).map((row) => [
      row.label,
      row.quantity,
      row.unit,
      formatPLN(row.unitPrice),
      formatPLN(row.total),
    ]),
    margin: { left: margin, right: margin },
    styles: { font: fontName, fontSize: 9, cellPadding: isTestDocument ? 1.5 : 2, overflow: 'linebreak' },
    headStyles: { fillColor: COLORS.dark, textColor: [255, 255, 255] },
    columnStyles: {
      0: { cellWidth: 72 },
      1: { halign: 'right', cellWidth: 24 },
      2: { cellWidth: 18 },
      3: { halign: 'right', cellWidth: 30 },
      4: { halign: 'right' },
    },
    alternateRowStyles: { fillColor: COLORS.light },
  });
  y = getLastTableY(doc) + (isTestDocument ? 4 : TABLE_GAP_AFTER);

  const minimumVisitNote = resolveMinimumVisitNote(data);
  if (minimumVisitNote) {
    y = drawNote(doc, fontName, y, margin, minimumVisitNote, isTestDocument);
  }

  y = drawSectionTitle(
    doc,
    fontName,
    y,
    margin,
    data.vatPercent > 0 ? 'Cena brutto dla klienta' : 'Cena dla klienta',
    isTestDocument
  );
  autoTable(doc, {
    startY: y,
    body: priceSummary.summaryRows.map((row) => [row.label, row.value]),
    margin: { left: margin, right: margin },
    styles: { font: fontName, fontSize: 9, cellPadding: isTestDocument ? 1.5 : 2 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 82 },
      1: { halign: 'right' },
    },
    alternateRowStyles: { fillColor: COLORS.light },
  });
  y = getLastTableY(doc) + (isTestDocument ? 4 : TABLE_GAP_AFTER);

  if (priceSummary.materialNote) {
    y = drawNote(doc, fontName, y, margin, priceSummary.materialNote, isTestDocument);
  } else if (data.materialLines.length > 0) {
    y = ensurePageSpace(doc, y, 24, margin);
    doc.setFont(fontName, 'bold');
    doc.setFontSize(12);
    doc.text(getClientMaterialModeLabel(data.materialMode), margin, y);
    y += 4;
    autoTable(doc, {
      startY: y,
      head: [['Materiał', 'Ilość szacunkowa', 'Cena dla klienta']],
      body: data.materialLines.map((line) => [
        line.label,
        line.quantityLabel,
        formatPLN(line.clientPrice),
      ]),
      margin: { left: margin, right: margin },
      styles: { font: fontName, fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: COLORS.dark, textColor: [255, 255, 255] },
      columnStyles: { 2: { halign: 'right' } },
    });
    y = getLastTableY(doc) + 5;
    y = drawNote(
      doc,
      fontName,
      y,
      margin,
      'Cena materiałów jest orientacyjna i przyjęta dla wskazanego standardu. Kolory intensywne, farby ceramiczne, produkty premium lub produkty wskazane przez klienta mogą zmienić koszt.',
      isTestDocument
    );
  }

  y = drawTitledNote(
    doc,
    fontName,
    y,
    margin,
    isTestDocument ? 'Zakres wyceny' : 'Uwagi do zakresu',
    isTestDocument
      ? 'Wycena obejmuje ustalony zakres prac. Prace dodatkowe, zmiana standardu materiałów lub warunki ujawnione po rozpoczęciu realizacji wymagają osobnego potwierdzenia.'
      : 'Oferta obejmuje ustalony zakres prac. Prace dodatkowe, zmiana standardu materiałów lub warunki ujawnione po rozpoczęciu realizacji wymagają osobnego potwierdzenia.',
    isTestDocument
  );

  // Prace objęte ceną, ale bez osobnej pozycji. Sekcja jest czysto informacyjna
  // i musi stać PRZED "Czego oferta nie obejmuje" — inaczej klient czyta, że
  // praca nie jest w zakresie, choć wykonawca oznaczył ją jako zawartą w cenie.
  const includedScopeLabels = data.includedScopeLabels ?? [];
  if (includedScopeLabels.length > 0) {
    y = drawTitledNote(
      doc,
      fontName,
      y,
      margin,
      'W cenie uwzględniono',
      `${includedScopeLabels.join('; ')}. Prace objęte ${isTestDocument ? 'wyceną' : 'ceną oferty'}, bez osobnej pozycji i bez dopłaty.`,
      isTestDocument
    );
  }

  if (data.excludedScopeLabels?.length) {
    y = drawTitledNote(doc, fontName, y, margin, 'Poza zakresem prac',
      `${data.excludedScopeLabels.join('; ')}. Wykonawca oznaczył te prace jako niedotyczące tej wyceny.`,
      isTestDocument);
  }
  if (data.unresolvedScopeLabels?.length) {
    y = drawTitledNote(doc, fontName, y, margin, 'Nieujęte w cenie — do ustalenia',
      `${data.unresolvedScopeLabels.join('; ')}. Te prace nie są ujęte w cenie i wymagają odrębnego potwierdzenia lub wyceny.`,
      isTestDocument);
  }

  y = drawTitledNote(
    doc,
    fontName,
    y,
    margin,
    isTestDocument ? 'Czego wycena nie obejmuje' : 'Czego oferta nie obejmuje',
    isTestDocument
      ? 'Wycena nie obejmuje prac niewskazanych w zakresie, zmian materiałów po akceptacji, napraw ukrytych uszkodzeń ani dodatkowych robót ujawnionych po rozpoczęciu prac.'
      : 'Oferta nie obejmuje prac niewskazanych w zakresie, zmian materiałów po akceptacji, napraw ukrytych uszkodzeń ani dodatkowych robót ujawnionych po rozpoczęciu prac.',
    isTestDocument
  );

  // One box for the closing block: a second box of its own was what pushed a
  // short offer onto a nearly empty second page.
  y = drawTitledNote(
    doc,
    fontName,
    y,
    margin,
    'Opcje do potwierdzenia',
    [
      'Przed rozpoczęciem prac warto potwierdzić ostateczny zakres, powierzchnie, standard materiałów, terminy oraz dostęp do pomieszczeń.',
      isTestDocument
        ? 'Wycena ważna 14 dni. Powierzchnie i zakres zostały oszacowane na podstawie dostępnych danych i wymagają potwierdzenia przed rozpoczęciem prac.'
        : 'Oferta ważna 14 dni. Powierzchnie i zakres zostały oszacowane na podstawie dostępnych danych i wymagają potwierdzenia przed rozpoczęciem prac.',
    ].join(' '),
    isTestDocument
  );

  drawFooter(doc, fontName, data);
  return doc;
}

export function buildClientOfferStructureSnapshot(
  input: ClientOfferPdfData
): ClientOfferStructureSnapshot {
  const data = sanitizeClientOfferDTO(input);
  const priceSummary = buildClientPdfSummaryRows({
    laborNet: data.workNet,
    materialsNet: data.materialNet,
    netTotal: data.netTotal,
    vatLabel: data.vatLabel,
    vatRate: data.vatPercent,
    vatValue: data.vatAmount,
    grossTotal: data.grossTotal,
    materialMode: data.materialMode,
  });

  return {
    projectName: data.projectName,
    materialMode: normalizeMaterialMode(data.materialMode),
    areaRows: buildAreaRows(data),
    itemRows: buildClientOfferItemRows(data),
    summaryRows: priceSummary.summaryRows,
    materialNote: priceSummary.materialNote,
    minimumVisitNote: resolveMinimumVisitNote(data),
    documentVariant: data.documentVariant ?? 'client',
    testNotices: resolveTestOfferNotices(data),
  };
}

function drawContractorHeader(
  doc: jsPDF,
  fontName: string,
  data: ClientOfferPdfData,
  margin: number,
  pageW: number,
  y: number
): number {
  const contractor = data.contractor;
  const isTestDocument = data.documentVariant === 'test';

  if (isTestDocument) {
    doc.setFont(fontName, 'bold');
    doc.setFontSize(17);
    doc.setTextColor(...COLORS.dark);
    doc.text(TEST_OFFER_TITLE, margin, y);
    doc.setFont(fontName, 'normal');
    doc.setFontSize(9);
    doc.text(TEST_OFFER_CONTRACTOR_LINE, margin, y + 7);
    doc.text(`Data: ${data.date.toLocaleDateString('pl-PL')}`, pageW - margin, y + 7, {
      align: 'right',
    });
    return y + 18;
  }

  const companyName = contractor?.companyName?.trim() ?? '';
  const showProLogo = Boolean(contractor?.isPro && contractor.logoDataUrl);

  if (showProLogo && contractor?.logoDataUrl) {
    try {
      doc.addImage(contractor.logoDataUrl, 'PNG', margin, y - 2, 18, 18);
      doc.setFont(fontName, 'bold');
      doc.setFontSize(15);
      doc.setTextColor(...COLORS.dark);
      doc.text(companyName, margin + 24, y + 5);
    } catch {
      doc.setFont(fontName, 'bold');
      doc.setFontSize(17);
      doc.setTextColor(...COLORS.dark);
      doc.text(companyName, margin, y);
    }
  } else {
    doc.setFont(fontName, 'bold');
    doc.setFontSize(17);
    doc.setTextColor(...COLORS.dark);
    doc.text(companyName, margin, y);
  }

  doc.setFont(fontName, 'normal');
  doc.setFontSize(9);
  const contactParts = [
    contractor?.nip ? `NIP: ${contractor.nip}` : undefined,
    contractor?.phone ? `Tel: ${contractor.phone}` : undefined,
    contractor?.email ? `E-mail: ${contractor.email}` : undefined,
  ].filter(Boolean) as string[];
  const contactLabel = contactParts.join('  |  ');
  const contactY = showProLogo ? y + 12 : y + 7;
  doc.text(contactLabel, margin, contactY);
  doc.text(`Data: ${data.date.toLocaleDateString('pl-PL')}`, pageW - margin, contactY, { align: 'right' });

  return y + 18;
}

function drawKeyValue(doc: jsPDF, fontName: string, y: number, x: number, label: string, value: string): number {
  doc.setTextColor(...COLORS.dark);
  doc.setFont(fontName, 'bold');
  doc.setFontSize(9);
  doc.text(`${label}:`, x, y);
  doc.setFont(fontName, 'normal');
  doc.text(value || '-', x + 48, y);
  return y + 6;
}

interface NoteLayout {
  lines: string[];
  width: number;
  height: number;
}

/**
 * Lines must be measured in the font they are printed in. Measuring before
 * setting it used whatever the previous element left behind (12 pt after a
 * section title), which wrapped notes at two thirds of the box.
 */
function layoutNote(doc: jsPDF, fontName: string, x: number, text: string, compact: boolean): NoteLayout {
  doc.setFont(fontName, 'normal');
  doc.setFontSize(8);
  const width = doc.internal.pageSize.getWidth() - x * 2;
  const lines = doc.splitTextToSize(text, width - 6) as string[];
  const height = Math.max(compact ? 10 : 12, lines.length * 4 + (compact ? 4 : 6));
  return { lines, width, height };
}

function drawNote(doc: jsPDF, fontName: string, y: number, x: number, text: string, compact = false): number {
  const note = layoutNote(doc, fontName, x, text, compact);
  y = ensurePageSpace(doc, y, note.height, x);

  doc.setFillColor(...COLORS.light);
  doc.rect(x, y, note.width, note.height, 'F');
  doc.setTextColor(...COLORS.dark);
  doc.setFont(fontName, 'normal');
  doc.setFontSize(8);
  doc.text(note.lines, x + 3, y + 6);
  return y + note.height + NOTE_GAP_AFTER;
}

/** A section title never stays at the bottom of a page without its note. */
function drawTitledNote(
  doc: jsPDF,
  fontName: string,
  y: number,
  margin: number,
  title: string,
  text: string,
  compact = false
): number {
  const note = layoutNote(doc, fontName, margin, text, compact);
  y = drawSectionTitle(doc, fontName, y, margin, title, compact, note.height);
  return drawNote(doc, fontName, y, margin, text, compact);
}

function formatPLN(value: number): string {
  return `${value.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;
}

function formatArea(value: number): string {
  return `${value.toLocaleString('pl-PL', { maximumFractionDigits: 1 })} m²`;
}

// Space after a table in the client offer; 6 mm keeps a one-item offer on one page.
const TABLE_GAP_AFTER = 6;
// Space a note leaves below itself, so the next title's capitals clear its box.
const NOTE_GAP_AFTER = 5;
// The table head plus its first row: a title above a table keeps at least that.
const TABLE_START_MIN_HEIGHT = 16;

function drawSectionTitle(
  doc: jsPDF,
  fontName: string,
  y: number,
  margin: number,
  title: string,
  compact = false,
  keepWithNextHeight = TABLE_START_MIN_HEIGHT
): number {
  const titleAdvance = compact ? 4 : 5;
  y = ensurePageSpace(doc, y, titleAdvance + keepWithNextHeight, margin);
  doc.setTextColor(...COLORS.dark);
  doc.setFont(fontName, 'bold');
  doc.setFontSize(compact ? 11 : 12);
  doc.text(title, margin, y);
  return y + titleAdvance;
}

function ensurePageSpace(doc: jsPDF, y: number, minHeight: number, margin: number): number {
  const pageH = doc.internal.pageSize.getHeight();
  if (y + minHeight <= pageH - margin) {
    return y;
  }

  doc.addPage();
  return margin;
}

function getLastTableY(doc: jsPDF): number {
  return (doc as jsPDF & { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY ?? 0;
}

function resolveClientTotalArea(data: ClientOfferPdfData): number | undefined {
  if (data.estimatedAreaM2 && data.estimatedAreaM2 > 0) {
    return data.estimatedAreaM2;
  }

  const itemArea = data.items
    .filter((item) => item.quantity > 0 && normalizeUnit(item.unit) === 'm2')
    .reduce((sum, item) => sum + item.quantity, 0);

  return itemArea > 0 ? itemArea : undefined;
}

function buildAreaRows(data: ClientOfferPdfData): string[][] {
  const scopeLabel = formatProjectScopeLabel(cleanClientScopeLabel(data.scopeLabel), 'do potwierdzenia');
  const rows: string[][] = [];
  const totalArea = resolveClientTotalArea(data);
  const surface = data.surfaceSummary;

  if (surface?.paving) {
    if (surface.pavingWorkingAreaM2 && surface.pavingWorkingAreaM2 > 0) {
      rows.push(['Powierzchnia do ułożenia', formatArea(surface.pavingWorkingAreaM2)]);
    }
    if (surface.curbLengthM && surface.curbLengthM > 0) {
      rows.push(['Obrzeża / krawężniki', `${String(surface.curbLengthM)} mb`]);
    }
    if (surface.pavingThicknessLabel) {
      rows.push(['Grubość kostki', surface.pavingThicknessLabel]);
    }
    if (surface.pavingSubbaseLabel) {
      rows.push(['Podbudowa', surface.pavingSubbaseLabel]);
    }
    if (surface.pavingTransportLabel) {
      rows.push(['Transport', surface.pavingTransportLabel]);
    }
    rows.push(['Status', surface.confirmationStatus || 'wycena robocza do potwierdzenia']);
    rows.push(['Zakres', scopeLabel]);
    rows.push(['Źródło danych', data.sourceLabel || 'dane ręczne / kreator']);
    return rows;
  }

  if (surface) {
    if (surface.surfaceMode === 'ready') {
      if (surface.finalWorkingAreaM2 && surface.finalWorkingAreaM2 > 0) {
        rows.push(['Gotowa powierzchnia robocza', formatArea(surface.finalWorkingAreaM2)]);
      }
      rows.push(['Status', surface.confirmationStatus || 'metraż zatwierdzony przez wykonawcę']);
    } else if (surface.surfaceMode === 'wall') {
      if (surface.wallGrossAreaM2 && surface.wallGrossAreaM2 > 0) {
        rows.push(['Powierzchnia ściany brutto', formatArea(surface.wallGrossAreaM2)]);
      }
      // An explicit 0 m² deduction is a result, not missing data — it stays on the
      // offer so the client can see the openings were counted as nothing.
      if (typeof surface.doorDeductionM2 === 'number') {
        rows.push(['Drzwi odliczone', formatArea(surface.doorDeductionM2)]);
      }
      if (typeof surface.largeOpeningsDeductionM2 === 'number') {
        rows.push(['Nietypowe otwory odliczone', formatArea(surface.largeOpeningsDeductionM2)]);
      }
      if (surface.finalWorkingAreaM2 && surface.finalWorkingAreaM2 > 0) {
        rows.push(['Powierzchnia do wyceny', formatArea(surface.finalWorkingAreaM2)]);
      }
    } else if (surface.surfaceMode === 'apartment' || surface.apartmentAreaM2) {
      if (surface.apartmentAreaM2 && surface.apartmentAreaM2 > 0) {
        rows.push(['Powierzchnia mieszkania', formatArea(surface.apartmentAreaM2)]);
      }
      if (surface.finalWorkingAreaM2 && surface.finalWorkingAreaM2 > 0) {
        rows.push(['Powierzchnia do wyceny', formatArea(surface.finalWorkingAreaM2)]);
      }
      rows.push(['Zakres powierzchni', getApartmentSurfaceScopeLabel(surface)]);
      rows.push(['Status', surface.confirmationStatus || 'wycena robocza do potwierdzenia']);
    } else if (surface.floorAreaM2 && surface.floorAreaM2 > 0) {
      rows.push(['Powierzchnia podłogi / sufitu', formatArea(surface.floorAreaM2)]);
      if (surface.finalWorkingAreaM2 && surface.finalWorkingAreaM2 > 0) {
        rows.push(['Powierzchnia do wyceny', formatArea(surface.finalWorkingAreaM2)]);
      }
      if (surface.ceilingAreaM2 && surface.ceilingAreaM2 > 0) {
        rows.push(['Zakres powierzchni', 'ściany + sufit']);
      }
    }
  } else if (data.floorAreaM2 && data.floorAreaM2 > 0) {
    rows.push(['Powierzchnia podłogi / sufitu', formatArea(data.floorAreaM2)]);
    rows.push([
      'Powierzchnia do wyceny',
      totalArea ? `ok. ${formatArea(totalArea)}` : 'do potwierdzenia',
    ]);
  }

  rows.push(['Zakres', scopeLabel]);
  rows.push(['Źródło danych', data.sourceLabel || 'dane ręczne / kreator']);

  return rows;
}

function getApartmentSurfaceScopeLabel(surface: ClientOfferSurfaceSummary): string {
  if (surface.bathroomModeLabel === 'ściany wyłączone, sufit w zakresie') {
    return 'całe mieszkanie, z wyłączeniem ścian łazienki; sufit łazienki w zakresie';
  }

  if (surface.bathroomModeLabel === 'całość w zakresie') {
    return 'całe mieszkanie, łazienka w zakresie';
  }

  return 'całe mieszkanie';
}

function drawFooter(doc: jsPDF, fontName: string, data: ClientOfferPdfData): void {
  if (data.contractor?.isPro) return;

  const pageCount = doc.getNumberOfPages();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
    doc.setPage(pageNumber);
    doc.setTextColor(148, 163, 184);
    doc.setFont(fontName, 'normal');
    doc.setFontSize(7);
    doc.text('Wygenerowano w ProWycenaapp.pl', pageW / 2, pageH - 8, { align: 'center' });
  }
}

function cleanClientScopeLabel(value: string): string {
  const cleaned = value
    .replace(/^\s*zakres\s*:\s*/i, '')
    .replace(/\bpodlodze\b/gi, 'podłodze')
    .replace(/\bpodlogi\b/gi, 'podłogi')
    .replace(/\bodswierznie\b/gi, 'odświeżenie')
    .replace(/\bodswierzenie\b/gi, 'odświeżenie')
    .replace(/\bodswiezenie\b/gi, 'odświeżenie')
    .replace(/\bgladz\b/gi, 'gładź')
    .replace(/\bprace\s+malarskie\b/gi, 'malowanie')
    .replace(/\b(?:pw|pow|powierzchnia)\.?\s*(?:po\s*)?(?:podłodze|podłogi)\s*\d+(?:[,.]\d+)?\s*m(?:2|²)?\b/gi, ' ')
    .replace(/\b\d+(?:[,.]\d+)?\s*m(?:2|²)?\s*(?:po\s*)?(?:podłodze|podłogi)\b/gi, ' ')
    .replace(/\b\d+(?:[,.]\d+)?\s*m(?:2|²)\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\s*,\s*/g, ', ')
    .replace(/^[,;\s-]+|[,;\s-]+$/g, '')
    .trim();

  return cleaned || 'do potwierdzenia';
}

function normalizeText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/ł/g, 'l')
    .replace(/Ł/g, 'L')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

function normalizeUnit(unit: string): string {
  return normalizeText(unit).replace(/[²2]/g, '2');
}
